import { type ReactNode } from "react";
import { StatusBadge } from "../../../components/index.ts";
import { DrawerSection, KeyValue } from "../DetailDrawer.tsx";
import type { Interaction, InteractionStep } from "../types.ts";
import type { FlowStep } from "./flowData.ts";

/**
 * The trace inspector for one selected flow step, shown in the right-side `DetailDrawer` (modelled
 * on the AgentDNA dashboard's InteractionDetail + step JSON card). It surfaces the selected hop's
 * request / response / model / latency / identity-trust-scope checks / metadata, rendering each
 * payload as escaped, collapsible JSON (Requirement 7.1, 7.2).
 *
 * Prop shape: `flowStep` always carries the from/to/verdict/checks/latency the rail selected, while
 * `step` is the rich payload-carrying `InteractionStep` (from `interaction.steps?.[0]`) when one is
 * attached. The optional `interaction` is the originating interaction so per-field fallback can reach
 * the interaction's data (Requirement 7.3). Pure presentation over already-fetched data — no fetch.
 */
export function TraceInspector(props: {
  /** The rich payload-carrying step, when the interaction attached one. */
  readonly step: InteractionStep | undefined;
  /** The selected timed hop — always present; carries verdict/checks/latency/endpoints. */
  readonly flowStep: FlowStep;
  /** The originating interaction, used for per-field payload fallback. */
  readonly interaction?: Interaction;
  readonly titleId: string;
  readonly onClose: () => void;
}): ReactNode {
  const { step, flowStep, interaction } = props;
  const blocked = flowStep.verdict === "blocked";

  // Per-field INDEPENDENT fallback (Requirement 7.3): the request value is the step's request when
  // present, else a generic representation derived from the interaction; the response value is the
  // step's response when present, else the interaction fallback. Each resolves independently of the
  // other — neither field's presence affects the other's resolution.
  const requestFallback = interactionRequestFallback(interaction);
  const responseFallback = interactionResponseFallback(interaction);
  const requestValue = step?.request !== undefined ? step.request : requestFallback;
  const responseValue = step?.response !== undefined ? step.response : responseFallback;

  const model = step?.model ?? "—";
  const latencyMs = step?.latencyMs ?? flowStep.latency;
  const checks = step?.checks ?? flowStep.checks;

  return (
    <>
      <header className="drawer-head">
        <div className="drawer-head__title">
          <h2 id={props.titleId} className="drawer-head__heading">
            Trace step
          </h2>
          <span className="drawer-head__id">{flowStep.spanId}</span>
        </div>
        {blocked ? (
          <StatusBadge level="error" label="Blocked" size="sm" />
        ) : (
          <StatusBadge level="healthy" label="Allowed" size="sm" />
        )}
        <button type="button" className="drawer-head__close" onClick={props.onClose} aria-label="Close trace">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>

      <div className="drawer-body">
        <DrawerSection title="Step">
          <KeyValue
            rows={[
              { k: "From", v: <code className="drawer-mono">{flowStep.from}</code> },
              { k: "To", v: <code className="drawer-mono">{flowStep.to}</code> },
              { k: "Direction", v: flowStep.dir },
              {
                k: "Verdict",
                v: (
                  <span className={blocked ? "drawer-threat" : "drawer-safe"}>
                    {flowStep.verdict}
                  </span>
                ),
              },
              { k: "Model", v: <code className="drawer-mono">{model}</code> },
              { k: "Latency", v: `${latencyMs} ms` },
              ...(flowStep.epoch !== undefined ? [{ k: "Epoch", v: String(flowStep.epoch) }] : []),
              { k: "Interaction ID", v: <code className="drawer-mono">{flowStep.interactionId}</code> },
            ]}
          />
        </DrawerSection>

        <DrawerSection title="Checks">
          <KeyValue
            rows={[
              { k: "Identity", v: <CheckValue ok={checks.identity} /> },
              { k: "Trust", v: <CheckValue ok={checks.trust} /> },
              { k: "Scope", v: <CheckValue ok={checks.scope} /> },
            ]}
          />
        </DrawerSection>

        <JsonSection title="Request" value={requestValue} />
        <JsonSection title="Response" value={responseValue} />

        {step?.metadata !== undefined && <JsonSection title="Metadata" value={step.metadata} />}
      </div>
    </>
  );
}

/** A single identity/trust/scope check rendered pass/fail. */
function CheckValue(props: { readonly ok: boolean }): ReactNode {
  return (
    <span className={props.ok ? "drawer-safe" : "drawer-threat"}>{props.ok ? "pass" : "fail"}</span>
  );
}

/**
 * A collapsible payload section rendering `value` as escaped JSON inside a `<details>` (Requirement
 * 7.2). When `value` is absent (nothing on the step and no interaction fallback) the section renders
 * an empty-state note rather than disappearing, so the operator can still see the field exists.
 */
function JsonSection(props: { readonly title: string; readonly value: unknown }): ReactNode {
  if (props.value === undefined) {
    return (
      <DrawerSection title={props.title}>
        <p className="adna-inspector__empty">No {props.title.toLowerCase()} payload.</p>
      </DrawerSection>
    );
  }
  return (
    <DrawerSection title={props.title}>
      <details className="adna-inspector__details" open>
        <summary className="adna-inspector__summary">{props.title} payload</summary>
        <pre className="adna-inspector__json drawer-raw">{renderPayload(props.value)}</pre>
      </details>
    </DrawerSection>
  );
}

/**
 * Render a payload safely as text (Requirement 7.2, 7.4). A raw string (the container passes raw
 * text through on malformed JSON) is shown as-is; anything else is pretty-printed. `JSON.stringify`
 * is guarded against circular references and other throws, falling back to `String(value)` so the
 * inspector never throws on odd input. React escapes the resulting text node, so there is no
 * `dangerouslySetInnerHTML` and no injection surface.
 */
function renderPayload(value: unknown): string {
  if (typeof value === "string") return value;
  try {
    const json = JSON.stringify(value, null, 2);
    return json ?? String(value);
  } catch {
    return String(value);
  }
}

/**
 * Per-field request fallback: when the selected step has no request payload, derive a generic
 * request-equivalent view from the interaction (its endpoints + intent). Returns `undefined` when
 * there is no interaction so the request section shows its empty state.
 */
function interactionRequestFallback(interaction: Interaction | undefined): unknown {
  if (interaction === undefined) return undefined;
  return {
    from: interaction.initiator.id,
    to: interaction.target.id,
    intent: interaction.intent.id,
  };
}

/**
 * Per-field response fallback, resolved independently of the request fallback: when the selected
 * step has no response payload, derive a generic response-equivalent view from the interaction (its
 * verdict-shaped outcome). Returns `undefined` when there is no interaction.
 */
function interactionResponseFallback(interaction: Interaction | undefined): unknown {
  if (interaction === undefined) return undefined;
  return {
    threat: interaction.threat,
    runtime: interaction.runtime,
    ...(interaction.blockType !== undefined ? { blockType: interaction.blockType } : {}),
  };
}
